import React from 'react';
import { ErrorState } from '../../ui/StateComponents';
import EmptyVideos from '../../ui/EmptyVideos';
import VirtualizedVideoGrid from '@/components/virtualized/VirtualizedVideoGrid';
import { VideoRowSkeleton } from '@/components/ui/skeleton/Skeleton';
import { useResponsiveColumns, VIDEO_GRID_COLUMNS } from '@/hooks/common/useResponsiveColumns';


interface UserVideo {
  id: string;
  video_url: string;
  thumbnail_url: string;
  title: string;
  file_size: number;
  created_at: string;
  updated_at: string;
  user_email?: string;
  user_id?: string;
  path?: string;
  status?: string;
  feedback?: string;
  prompt?: string;
  model?: string;
  video_type?: string;
  is_pinned?: boolean;
  pinned_by?: string;
  pinned_at?: string;
  aspect_ratio?: string;
  original_video_id?: string;
}

interface VideoGridProps {
  videos: UserVideo[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error?: string | null;
  hasMore: boolean;
  totalCount: number;
  onVideoClick: (video: UserVideo) => void;
  // onPinVideo?: (video: UserVideo) => void;
  // onUnpinVideo?: (video: UserVideo) => void;
  onRetry?: () => void;
  getStatusBadge: (status?: string) => React.ReactNode;
  // isAdmin?: boolean;
  onFavouriteSuccess?: (isFavourited: boolean) => void;
  // onArchiveSuccess?: (isArchived: boolean) => void;
}

/**
 * VideoGrid component for admin content review
 * Now uses virtualization for efficient rendering of large video lists
 */
const VideoGrid: React.FC<VideoGridProps> = ({
  videos,
  isLoading,
  isLoadingMore,
  error,
  hasMore,
  totalCount,
  onVideoClick,
  // onPinVideo,
  // onUnpinVideo,
  onRetry,
  getStatusBadge,
  // isAdmin = false,
  onFavouriteSuccess,
  // onArchiveSuccess,
}) => {
  const columns = useResponsiveColumns(VIDEO_GRID_COLUMNS);

  // Loading state with skeleton
  if (isLoading && videos.length === 0) {
    return (
      <div className="px-4 lg:px-8">
        <div className="space-y-4">
          {['video-skeleton-0', 'video-skeleton-1', 'video-skeleton-2', 'video-skeleton-3'].map((key, i) => (
            <VideoRowSkeleton key={key} columns={columns} rowIndex={i} />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <ErrorState 
        error={error} 
        onRetry={onRetry} 
      />
    );
  }

  // Empty state
  if (!isLoading && videos.length === 0) {
    return <EmptyVideos icon="video" title="No videos found" description="No videos match your current filters" />;
  }


  return (
    <div className="px-4 lg:px-8">
      {/* Virtualized Video Grid */}
      <VirtualizedVideoGrid
        videos={videos}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        onVideoClick={onVideoClick}
        // do onPinVideo={onPinVideo}
        // onUnpinVideo={onUnpinVideo}
        getStatusBadge={getStatusBadge}
        // isPinnedSection={false}
        // isAdmin={isAdmin}
        onFavouriteSuccess={onFavouriteSuccess}
        // onArchiveSuccess={onArchiveSuccess}
        className="mb-8"
      />

      {/* Results Summary */}
      <div className="text-center text-sm text-gray-600 mt-4">
        Showing {videos.length} of {totalCount} videos
        {hasMore && (
          <span className="ml-2 text-blue-600">
            (Scroll for more)
          </span>
        )}
      </div>
    </div>
  );
};

export default VideoGrid;
